import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Flex, useToast, Skeleton, Switch, Icon } from '@chakra-ui/react';
import { PiIdentificationBadge } from 'react-icons/pi';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { ShortcutCard } from '../Home/components/ShortcutCard';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ReactComponent as TeamShirtIcon } from '@/assets/icons/TeamShirt.svg';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { t } from '@/i18n';

type Link = {
  label: string;
  url: string;
  icon: ReactNode;
};

const links: Array<Link> = [
  {
    label: t('pages.application_team.application_team_roster_group_link'),
    url: '/application/team/roster-group',
    icon: <Icon as={TeamShirtIcon} />,
  },
  {
    label: t('pages.application_team.application_team_roster_member_link'),
    url: '/application/team/roster-member',
    icon: <PiIdentificationBadge />,
  },
];

export const ApplicationTeam = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    undefined,
    ApplicationComponentId.TEAM,
  );

  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_team.application_team_enable_success')
      : t('pages.application_team.application_team_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_team.application_team_enable_error')
      : t('pages.application_team.application_team_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation(
      [{ component_id: ApplicationComponentId.ROSTER, active: pendingAppAreaState }],
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: successActionString,
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: errorActionString,
          });

          setIsAppAreaActive(!pendingAppAreaState);
        },
      },
    );

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_team.application_team_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_team.application_team_label')}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_team.application_team_enabled')
                  : t('pages.application_team.application_team_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        <Flex gap='xxsmall' flexWrap='wrap'>
          {links?.map(link => (
            <Link key={link.url} to={link.url}>
              <ShortcutCard label={t(link.label)} icon={link.icon} />
            </Link>
          ))}
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_team.application_team_confirm_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_team.application_team_confirm_enable')
              : t('pages.application_team.application_team_confirm_disable')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
